import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { getLanguageCode, hasCallToAction } from '../utils';

import MainNavigationLinkItem from './MainNavigationLinkItem';
import MainNavigationLinkStyle from './MainNavigationLinkStyle';
import CTAAnchor from './CTAAnchor';
import Block from './Block';

const NavBlock = styled(Block)`
  display: block;

  ${breakpoint('tablet')`
    display: flex;
  `};
`;

const ExternalLink = styled.a`
  ${MainNavigationLinkStyle}

  ${breakpoint('tablet')`
    font-size: ${({ theme }) => theme.spacing.baseDown1};
  `};
`;

const MainNavigation = ({
  selectedLocale,
  isSticky,
  location,
  linkcolor,
  onNavigation,
}) => {
  const { allPrismicMainNavigation } = useStaticQuery(
    graphql`
      query {
        allPrismicMainNavigation {
          edges {
            node {
              lang
              data {
                body {
                  ... on PrismicMainNavigationBodyLink {
                    id
                    primary {
                      title
                      href {
                        link_type
                        url
                        target
                      }
                    }
                    items {
                      sublink_title
                      sublink_href {
                        link_type
                        url
                        target
                      }
                    }
                  }
                }
                call_to_action_color
                call_to_action_text {
                  text
                }
                call_to_action_link {
                  link_type
                  url
                  lang
                  target
                }
              }
            }
          }
        }
      }
    `,
  );

  const localeNavigation = allPrismicMainNavigation.edges.find(
    ({ node }) => node.lang === selectedLocale,
  );
  const {
    node: { data: content },
  } = localeNavigation;

  return (
    <NavBlock alignItems="center">
      {content.body.map((item) => {
        const localePath =
          selectedLocale === 'en-us'
            ? ''
            : `/${getLanguageCode(selectedLocale)}`;

        return (
          <MainNavigationLinkItem
            key={`nav-link-${item.primary.href.url}`}
            content={item}
            localePath={localePath}
            isSticky={isSticky}
            linkcolor={linkcolor}
            isActive={location.pathname === `${item.primary.href.url}/`}
            onNavigation={onNavigation}
          />
        );
      })}

      {hasCallToAction(content) ? (
        <Block
          display={{ mobile: 'flex', tablet: 'block' }}
          py={{ mobile: 'baseUnit', tablet: '0' }}
          px="baseUnit"
          alignItems="center"
          justifyContent="center"
          style={{ whiteSpace: 'nowrap' }}
        >
          <CTAAnchor
            text={content.call_to_action_text.text}
            link={content.call_to_action_link}
            colorTheme={content.call_to_action_color}
          />
        </Block>
      ) : null}

      {content.body.map(({ primary: { title, href } }) => (
        <Fragment key={`nav-link-${href.url}`}>
          {href.link_type === 'Web' ? (
            <ExternalLink href={href.url} linkcolor={linkcolor}>
              {title}
            </ExternalLink>
          ) : null}
        </Fragment>
      ))}
    </NavBlock>
  );
};

MainNavigation.propTypes = {
  selectedLocale: PropTypes.string.isRequired,
  isSticky: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  linkcolor: PropTypes.string.isRequired,
  onNavigation: PropTypes.func.isRequired,
};

export default MainNavigation;
